import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { AddCircle, Edit2, Eye, EyeSlash, Message, SearchNormal1, Trash, VideoPlay, Warning2 } from "iconsax-reactjs";
import {
  subscribeReels,
  setReelStatus,
  updateReelCaption,
  deleteReel,
  formatCount,
  ReelPropertyUnavailableError,
} from "../utils/reelsShared";
import ReelCard from "./reels/ReelCard";
import ReelVideoModal from "./reels/ReelVideoModal";
import ReelCommentsModal from "./reels/ReelCommentsModal";
import ReelConfirmModal from "./reels/ReelConfirmModal";
import ReelCaptionModal from "./reels/ReelCaptionModal";
import CreateReelModal from "./reels/CreateReelModal";
import "./DealerReels.css";

const STATUS_TABS = [
  { id: "all", label: "All" },
  { id: "active", label: "Live" },
  { id: "inactive", label: "Hidden" },
];

/**
 * The dealer's own reels, the web counterpart of My Reels in the app: manage
 * what is already published, or publish a new one (CreateReelModal; trimming
 * stays in the app, so web uploads must already be under 90 seconds).
 */
export default function DealerReels({ user }) {
  const dealerId = user?.uid;

  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [previewReel, setPreviewReel] = useState(null);
  const [commentsReel, setCommentsReel] = useState(null);
  const [captionReel, setCaptionReel] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [properties, setProperties] = useState({});
  const requestedProperties = useRef(new Set());

  useEffect(() => {
    if (!dealerId) return undefined;
    setLoading(true);
    const unsubscribe = subscribeReels(
      { dealerId },
      (list) => { setReels(list); setLoading(false); setLoadError(null); },
      () => { setLoadError("Could not load your reels. Reload the page to try again."); setLoading(false); }
    );
    return () => unsubscribe();
  }, [dealerId]);

  // Listing titles, resolved once per id ever seen.
  useEffect(() => {
    reels.forEach((reel) => {
      const pid = reel.propertyId;
      if (!pid || requestedProperties.current.has(pid)) return;
      requestedProperties.current.add(pid);
      getDoc(doc(db, "properties", pid))
        .then((snap) => {
          const data = snap.exists() ? snap.data() : null;
          setProperties((prev) => ({
            ...prev,
            [pid]: !data || data.status === "deleted"
              ? "Listing removed"
              : (data.title || "Untitled listing"),
          }));
        })
        .catch(() => setProperties((prev) => ({ ...prev, [pid]: "" })));
    });
  }, [reels]);

  const totals = useMemo(() => reels.reduce(
    (acc, r) => ({
      views: acc.views + (r.views || 0),
      saves: acc.saves + Math.max(0, r.saves || 0),
      enquiries: acc.enquiries + (r.enquiries || 0),
    }),
    { views: 0, saves: 0, enquiries: 0 }
  ), [reels]);

  const filteredReels = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    return reels.filter((reel) => {
      if (activeTab === "active" && reel.status !== "active") return false;
      if (activeTab === "inactive" && reel.status === "active") return false;
      if (!term) return true;
      const haystack = [reel.caption, reel.city, reel.locality, properties[reel.propertyId]]
        .filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(term);
    });
  }, [reels, activeTab, searchQuery, properties]);

  const handleToggleStatus = async (reel) => {
    const nextStatus = reel.status === "active" ? "inactive" : "active";
    setBusyId(reel.id);
    try {
      await setReelStatus(reel, nextStatus);
    } catch (err) {
      if (err instanceof ReelPropertyUnavailableError) {
        alert("That listing was deleted, so this reel can no longer be published.");
      } else {
        console.error("Failed to update reel status:", err);
        alert("Could not update the reel status. Please try again.");
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleSaveCaption = async (caption) => {
    if (!captionReel) return;
    setBusyId(captionReel.id);
    try {
      await updateReelCaption(captionReel.id, caption);
      setCaptionReel(null);
    } catch (err) {
      console.error("Failed to update caption:", err);
      alert("Could not save the caption. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setBusyId(pendingDelete.id);
    try {
      await deleteReel(pendingDelete);
      setPendingDelete(null);
    } catch (err) {
      console.error("Failed to delete reel:", err);
      alert("Could not delete the reel. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const tabCounts = useMemo(() => {
    const live = reels.filter((r) => r.status === "active").length;
    return { all: reels.length, active: live, inactive: reels.length - live };
  }, [reels]);

  const openCreate = () => setShowCreateModal(true);

  return (
    <div className="dealer-reels">
      <div className="stat-strip">
        <div className="stat">
          <span className="stat-label">Views</span>
          <span className="stat-value">{formatCount(totals.views)}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Saves</span>
          <span className="stat-value">{formatCount(totals.saves)}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Enquiries</span>
          <span className="stat-value">{formatCount(totals.enquiries)}</span>
        </div>
      </div>

      <div className="toolbar">
        <div className="search-field dealer-reels-search">
          <SearchNormal1 size={16} variant="Linear" aria-hidden="true" />
          <input
            type="search"
            className="input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search caption, listing or city"
            aria-label="Search your reels"
          />
        </div>
        <div className="segmented" role="group" aria-label="Filter by status">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={activeTab === tab.id ? "active" : ""}
              aria-pressed={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {!loading && <span className="reel-seg-count">{formatCount(tabCounts[tab.id])}</span>}
            </button>
          ))}
        </div>
        <span className="toolbar-spacer" />
        <button type="button" className="btn-primary dealer-reels-upload" onClick={openCreate}>
          <AddCircle size={18} variant="Linear" aria-hidden="true" />
          Upload reel
        </button>
      </div>

      {loading ? (
        <div className="reel-grid" aria-busy="true">
          <span className="sr-only" role="status">Loading your reels</span>
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="reel-card reel-card-skeleton" aria-hidden="true">
              <div className="skeleton reel-skel-poster" />
              <div className="reel-card-body">
                <div className="skeleton reel-skel-line is-short" />
                <div className="skeleton reel-skel-line is-wide" />
                <div className="skeleton reel-skel-line is-mid" />
              </div>
            </div>
          ))}
        </div>
      ) : loadError ? (
        <p className="reel-error-banner" role="alert">
          <Warning2 size={18} variant="Linear" aria-hidden="true" />
          {loadError}
        </p>
      ) : filteredReels.length === 0 ? (
        <div className="empty-state reel-empty">
          <span className="empty-state-icon" aria-hidden="true">
            <VideoPlay size={22} variant="Linear" />
          </span>
          <h3>{reels.length === 0 ? "No reels yet" : "No reels match"}</h3>
          <p>
            {reels.length === 0
              ? "Upload a short video of a listing; reels appear in the app's Reels tab and bring enquiries."
              : "Try a different search term or status filter."}
          </p>
          {reels.length === 0 ? (
            <button type="button" className="btn-tonal" onClick={openCreate}>
              <AddCircle size={18} variant="Linear" aria-hidden="true" />
              Upload reel
            </button>
          ) : (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => { setSearchQuery(""); setActiveTab("all"); }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="reel-grid">
          {filteredReels.map((reel) => {
            const busy = busyId === reel.id;
            const isLive = reel.status === "active";
            return (
              <ReelCard
                key={reel.id}
                reel={reel}
                propertyTitle={properties[reel.propertyId]}
                onPreview={setPreviewReel}
                actions={
                  <>
                    <button
                      type="button"
                      className="btn-icon"
                      onClick={() => setCommentsReel(reel)}
                      aria-label={`Comments (${formatCount(reel.commentsCount)})`}
                      title="Comments"
                    >
                      <Message size={18} variant="Linear" />
                    </button>
                    <button
                      type="button"
                      className="btn-icon"
                      onClick={() => setCaptionReel(reel)}
                      disabled={busy}
                      aria-label="Edit caption"
                      title="Edit caption"
                    >
                      <Edit2 size={18} variant="Linear" />
                    </button>
                    <button
                      type="button"
                      className="btn-icon"
                      onClick={() => handleToggleStatus(reel)}
                      disabled={busy}
                      aria-label={isLive ? "Hide from feed" : "Publish to feed"}
                      title={isLive ? "Hide from feed" : "Publish to feed"}
                    >
                      {isLive ? <EyeSlash size={18} variant="Linear" /> : <Eye size={18} variant="Linear" />}
                    </button>
                    <button
                      type="button"
                      className="btn-icon reel-action-danger"
                      onClick={() => setPendingDelete(reel)}
                      disabled={busy}
                      aria-label="Delete reel"
                      title="Delete reel"
                    >
                      <Trash size={18} variant="Linear" />
                    </button>
                  </>
                }
              />
            );
          })}
        </div>
      )}

      {previewReel && <ReelVideoModal reel={previewReel} onClose={() => setPreviewReel(null)} />}
      {commentsReel && <ReelCommentsModal reel={commentsReel} onClose={() => setCommentsReel(null)} />}

      {captionReel && (
        <ReelCaptionModal
          reel={captionReel}
          busy={busyId === captionReel.id}
          onSave={handleSaveCaption}
          onClose={() => setCaptionReel(null)}
        />
      )}

      <ReelConfirmModal
        open={!!pendingDelete}
        title="Delete this reel?"
        message="The video, its cover, its comments and all of its stats will be permanently removed. This cannot be undone."
        confirmLabel="Delete reel"
        busy={!!pendingDelete && busyId === pendingDelete.id}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <CreateReelModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        user={user}
      />
    </div>
  );
}
